import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, ActivityIndicator, ScrollView, TouchableOpacity } from 'react-native';
import { Picker } from '@react-native-picker/picker'; 
import { FontAwesome5 } from '@expo/vector-icons';
import { useAlertModal } from '../../common/hooks';
import { getProductCategoriesData, getProductFamiliesData, getProductLinesData, QuickAddProduct, QuickAddProductManual, deleteProduct } from '../../api/Product';
import { getLocationsData } from '../../api/Settings';
import BasicLayout from '../../common/components/CustomLayout/BasicLayout';
import { BOHTable, BOHTHead, BOHTBody, BOHTR, BOHTH, BOHTD } from '../../common/components/bohtable';
import { BOHButton, BOHToolbar } from '../../common/components/bohtoolbar';
import { CommonContainer } from '../../common/components/CustomLayout';
import { productsStyle } from './product/products/styles/ProductsStyle';
import { consumeQuickScanReturnSubItem, navigateBackToInventory } from './inventoryNavigationState';

const QuickScanScreen = ({ navigation }) => {
  const { showAlert } = useAlertModal();
  const [barcode, setBarcode] = useState('');
  const [scanHistory, setScanHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Template state
  const [categories, setCategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const [lines, setLines] = useState([]);
  const [locations, setLocations] = useState([]);

  const [selectedCatId, setSelectedCatId] = useState(0);
  const [selectedFamId, setSelectedFamId] = useState(0);
  const [selectedLineId, setSelectedLineId] = useState(0);
  const [selectedHomeLoc, setSelectedHomeLoc] = useState(0);
  const [selectedCurrLoc, setSelectedCurrLoc] = useState(0);

  // Inline Validation state
  const [errors, setErrors] = useState({
    category: false,
    family: false,
    line: false,
  });

  const inputRef = useRef(null);

  useEffect(() => {
    getProductCategoriesData((res) => setCategories(res || []));
    getLocationsData({}, (res) => {
        setLocations(res || []);
        setSelectedHomeLoc(0);
        setSelectedCurrLoc(0);
    });
    inputRef.current?.focus();
  }, []);

  const onCatChange = (catId) => {
    setSelectedCatId(catId);
    setErrors(prev => ({...prev, category: false}));
    setSelectedFamId(0);
    setSelectedLineId(0);
    setFamilies([]);
    setLines([]);
    if (catId > 0) {
      getProductFamiliesData(catId).then(res => res.json()).then(json => {
        setFamilies(json || []);
        if (json?.length > 0) {
            onFamChange(json[0].id);
        }
      });
    }
  };

  const onFamChange = (famId) => {
    setSelectedFamId(famId);
    setErrors(prev => ({...prev, family: false}));
    setSelectedLineId(0);
    setLines([]);
    if (famId > 0) {
      getProductLinesData(famId).then(res => res.json()).then(json => {
        setLines(json || []);
        if (json?.length > 0) {
            setSelectedLineId(json[0].id);
        }
      });
    }
  };

  const handleScan = () => {
    let newErrors = {
        category: selectedCatId <= 0,
        family: selectedFamId <= 0,
        line: selectedLineId <= 0
    };

    setErrors(newErrors);

    if (newErrors.category || newErrors.family || newErrors.line) {
        return; 
    }

    const cleanBarcode = barcode.trim();
    if (!cleanBarcode || loading) return;

    setLoading(true);
    const lineObj = lines.find(l => l.id === selectedLineId);

    const addPayload = {
      rowcounts: 1,
      category_id: selectedCatId,
      family_id: selectedFamId,
      line_id: selectedLineId,
      line: lineObj,
      size: lineObj?.size,
      shortcode: lineObj?.shortcode,
      home_location: selectedHomeLoc,
      current_location: selectedCurrLoc,
      status: 0, 
      item_id: '',
      barcode: cleanBarcode
    };


    QuickAddProductManual(addPayload, (addRes, addStatus) => {
      setLoading(false);
      if (addStatus === 201 && addRes.product) {
        const newProd = addRes.product;
        setScanHistory(prev => [{
          id: Date.now().toString(),
          product_id: newProd.id,
          barcode: newProd.barcode,
          category_name: categories.find(c => c.id == selectedCatId)?.category,
          family_name: families.find(f => f.id == selectedFamId)?.family,
          line_name: lineObj?.line,
          status: 'Ready',
          size: lineObj?.size || '-',
          timestamp: new Date().toLocaleTimeString(),
          home_name: getLocationLabel(locations.find(l => l.id == selectedHomeLoc)),
          current_name: getLocationLabel(locations.find(l => l.id == selectedCurrLoc))
        }, ...prev]);
        setBarcode('');
        setTimeout(() => inputRef.current?.focus(), 100);
      } else {
        showAlert('error', addRes.message || addRes.error || 'Failed to create product');
        setBarcode('');
        inputRef.current?.focus();
      }
    });
  };

  const getLocationLabel = (loc) => {
    if (!loc) return '-';
    return `${loc?.name ? loc.name + ' - ' : ''}${loc?.location ?? ''}`;
  }

  const handleDeleteProduct = (productId, historyId) => {
    if (!productId) return;
    deleteProduct(productId, (res, status) => {
      if (status === 200 || status === 201) {
        setScanHistory(prev => prev.filter(item => item.id !== historyId));
        // showAlert('success', 'Product deleted successfully'); // disabled to prevent modal alert spam for fast workflow
      } else {
        showAlert('error', res?.error || 'Failed to delete product');
      }
    });
  };

  const tableWidths = [160, 180, 160, 100, 120, 260, 260, 130, 80];

  const handleGoBack = () => {
    const returnSubItem = consumeQuickScanReturnSubItem();
    navigateBackToInventory(navigation, returnSubItem ?? null);
  };

  return (
    <BasicLayout navigation={navigation} screenName="Inventory Quick Scan" goBack={handleGoBack}>
      <CommonContainer>
        {/* SELECTORS */}
        <View style={styles.headerContainer}>
          <View style={styles.gridRow}>
            {/* Category */}
            <View style={styles.gridCol}>
              <View style={styles.inputWrapper}>
                <Text style={styles.inputLabel}>Category</Text>
                <View style={styles.pickerContainer}>
                  <Picker selectedValue={selectedCatId} onValueChange={onCatChange} style={styles.pickerStyle}>
                    <Picker.Item label="" value={0} />
                    {categories.map(c => <Picker.Item key={c.id} label={c.category} value={c.id} />)}
                  </Picker>
                </View>
              </View>
              {errors.category && <Text style={styles.errorText}>Please select a category.</Text>}
            </View>

            {/* Family */}
            <View style={styles.gridCol}>
              <View style={styles.inputWrapper}>
                <Text style={styles.inputLabel}>Family</Text>
                <View style={styles.pickerContainer}>
                  <Picker selectedValue={selectedFamId} onValueChange={onFamChange} style={styles.pickerStyle}>
                     <Picker.Item label="" value={0} />
                     {families.map(f => <Picker.Item key={f.id} label={f.family} value={f.id} />)}
                  </Picker>
                </View>
              </View>
              {errors.family && <Text style={styles.errorText}>Please select a family.</Text>}
            </View>

            {/* Line */}
            <View style={styles.gridCol}>
              <View style={styles.inputWrapper}>
                <Text style={styles.inputLabel}>Line</Text>
                <View style={styles.pickerContainer}>
                  <Picker selectedValue={selectedLineId} onValueChange={(v) => {setSelectedLineId(v); setErrors(prev => ({...prev, line: false}))}} style={styles.pickerStyle}>
                     <Picker.Item label="" value={0} />
                     {lines.map(l => <Picker.Item key={l.id} label={l.line + (l.size ? ' ' + l.size : '')} value={l.id} />)}
                  </Picker>
                </View>
              </View>
              {errors.line && <Text style={styles.errorText}>Please select a line.</Text>}
            </View>
          </View>

          <View style={[styles.gridRow, {marginTop: 15}]}>

            {/* Home */}
            <View style={styles.gridCol}>
              <View style={styles.inputWrapper}>
                <Text style={styles.inputLabel}>Home</Text>
                <View style={styles.pickerContainer}>
                  <Picker selectedValue={selectedHomeLoc} onValueChange={(v) => setSelectedHomeLoc(v)} style={styles.pickerStyle}>
                      <Picker.Item label="" value={0} />
                      {locations.map(loc => <Picker.Item key={loc.id} label={getLocationLabel(loc)} value={loc.id} />)}
                  </Picker>
                </View>
              </View>
            </View>

            {/* Current */}
            <View style={styles.gridCol}>
              <View style={styles.inputWrapper}>
                <Text style={styles.inputLabel}>Current</Text>
                <View style={styles.pickerContainer}>
                  <Picker selectedValue={selectedCurrLoc} onValueChange={(v) => setSelectedCurrLoc(v)} style={styles.pickerStyle}>
                      <Picker.Item label="" value={0} />
                      {locations.map(loc => <Picker.Item key={loc.id} label={getLocationLabel(loc)} value={loc.id} />)}
                  </Picker>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* SCAN SECTION */}
        <View style={styles.scanSection}>
           <Text style={styles.scanLabel}>Scan Barcode to Add Item</Text>
           <View style={styles.scanRow}>
            <TextInput 
              ref={inputRef} 
              style={[productsStyle.searchInput, {flex: 1, height: 40, fontSize: 16, borderColor: '#007bff', borderWidth: 2}]} 
              value={barcode} 
              onChangeText={setBarcode} 
              placeholder="Scan or type barcode..." 
              onSubmitEditing={handleScan}
              autoFocus
            />
            <BOHButton label="Scan" onPress={handleScan} disabled={loading} style={{marginLeft: 10, minWidth: 100}} />
            <BOHButton label="Clear History" onPress={() => setScanHistory([])} type="danger" style={{marginLeft: 10}} />
            {loading && <ActivityIndicator size="small" color="#007bff" style={{marginLeft: 15}} />}
          </View>
        </View>

        <BOHToolbar><Text style={styles.historyTitle}>Added Items ({scanHistory.length})</Text></BOHToolbar>

        <ScrollView horizontal={true}>
          <BOHTable style={{width: 1550}}>
            <BOHTHead><BOHTR>
              <BOHTH width={tableWidths[0]}>Category</BOHTH>
              <BOHTH width={tableWidths[1]}>Family</BOHTH>
              <BOHTH width={tableWidths[2]}>Line</BOHTH>
              <BOHTH width={tableWidths[3]}>Status</BOHTH>
              <BOHTH width={tableWidths[4]}>Size</BOHTH>
              <BOHTH width={tableWidths[5]}>Home</BOHTH>
              <BOHTH width={tableWidths[6]}>Current</BOHTH>
              <BOHTH width={tableWidths[7]}>Barcode</BOHTH>
              <BOHTH width={tableWidths[8]}>Action</BOHTH>
            </BOHTR></BOHTHead>
            <BOHTBody>
              {scanHistory.map(item => (
                <BOHTR key={item.id}>
                  <BOHTD width={tableWidths[0]} style={{fontSize: 13}}>{item.category_name}</BOHTD>
                  <BOHTD width={tableWidths[1]} style={{fontSize: 13}}>{item.family_name}</BOHTD>
                  <BOHTD width={tableWidths[2]} style={{fontSize: 13}}>{item.line_name}</BOHTD>
                  <BOHTD width={tableWidths[3]} style={{fontSize: 13}}>{item.status}</BOHTD>
                  <BOHTD width={tableWidths[4]} style={{fontSize: 13}}>{item.size}</BOHTD>
                  <BOHTD width={tableWidths[5]} style={{fontSize: 12}}>{item.home_name}</BOHTD>
                  <BOHTD width={tableWidths[6]} style={{fontSize: 12}}>{item.current_name}</BOHTD>
                  <BOHTD width={tableWidths[7]} style={{fontWeight: 'bold', color: '#007bff', fontSize: 13}}>{item.barcode}</BOHTD>
                  <BOHTD width={tableWidths[8]}>
                    <TouchableOpacity onPress={() => handleDeleteProduct(item.product_id, item.id)} style={{padding: 5}}>
                      <FontAwesome5 name="trash" size={16} color="red" />
                    </TouchableOpacity>
                  </BOHTD>
                </BOHTR>
              ))}
              {scanHistory.length === 0 && (
                 <BOHTR><BOHTD width={1550} style={{textAlign: 'center', padding: 20, color: '#999'}}>No items added yet. Set options above and scan.</BOHTD></BOHTR>
              )}
            </BOHTBody>
          </BOHTable>
        </ScrollView>
      </CommonContainer>
    </BasicLayout>
  );
};

const styles = StyleSheet.create({
  headerContainer: { padding: 15, backgroundColor: '#fff' },
  gridRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' },
  gridCol: { flex: 1, paddingHorizontal: 10 },
  inputWrapper: { flexDirection: 'row', alignItems: 'center', width: '100%' },
  inputLabel: { width: 70, fontSize: 13, color: '#333', textAlign: 'right', paddingRight: 8 },
  pickerContainer: { flex: 1 },
  pickerStyle: { width: '100%', backgroundColor: 'white', borderWidth: 1, borderColor: '#ccc', borderRadius: 5, height: 40 },
  
  scanSection: { padding: 15, paddingTop: 5, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#eee' },
  scanLabel: { fontSize: 13, fontWeight: 'bold', marginBottom: 5, color: '#333' },
  scanRow: { flexDirection: 'row', alignItems: 'center' },
  
  errorText: { color: 'red', fontSize: 11, marginLeft: 70, marginTop: 4 },
  historyTitle: { fontSize: 16, fontWeight: '700' }
});

export default QuickScanScreen;
